<script setup lang="ts">
import { computed } from 'vue';
import { approvalLabel, type NyxApprovalAction } from '../lib/nyx-presentation.js';
import AppIcon from './AppIcon.vue';
import type { IconName } from './icons.js';

/**
 * A request for permission from the client. Only pending requests offer a
 * decision; earlier ones show how they were decided.
 */
const props = defineProps<{
  action: NyxApprovalAction;
  summary: string;
  pending: boolean;
  decision: boolean | null;
  canDecide: boolean;
  disabled: boolean;
}>();
const emit = defineEmits<{ decide: [approve: boolean] }>();
const icons: Readonly<Record<NyxApprovalAction, IconName>> = {
  command: 'terminal',
  file_change: 'edit',
  permission: 'lock',
  other: 'warning',
};
const open = computed(() => props.pending && props.decision === null);
</script>

<template>
  <article class="nyx-card" data-kind="approval" :data-pending="open ? 'true' : 'false'">
    <header class="nyx-card-head">
      <span class="nyx-card-icon" aria-hidden="true"><AppIcon :name="icons[action]" /></span>
      <strong>{{ approvalLabel[action] }}</strong>
      <span v-if="decision === true" class="tag" data-tone="ok">Allowed</span>
      <span v-else-if="decision === false" class="tag" data-tone="danger">Denied</span>
      <span v-else-if="!pending" class="tag">No longer pending</span>
      <span v-else class="tag" data-tone="warn">Waiting</span>
    </header>
    <pre class="nyx-code">{{ summary }}</pre>
    <footer v-if="open" class="nyx-card-actions">
      <template v-if="canDecide">
        <button type="button" class="small" :disabled="disabled" @click="emit('decide', false)">
          <AppIcon name="close" />Don't allow
        </button>
        <button
          type="button"
          class="primary small"
          :disabled="disabled"
          @click="emit('decide', true)"
        >
          <AppIcon name="check" />Allow once
        </button>
      </template>
      <span v-else class="hint">Decide in Nyx Link on the host. This view has read access.</span>
    </footer>
  </article>
</template>
